import React, { useState, useEffect, useRef } from "react";
import { InstantSearch, Configure, useInstantSearch, Index } from "react-instantsearch";
import { DOCTORS, DOCTORS_SORT, algoliaAttributes, leumitSearchClient, localAttributes } from "assets/constants";
import "instantsearch.css/themes/satellite.css";
import "./DoctorsSearchResults.css";
import { TopRefinement } from "./topRefinement/TopRefinement";
import strings from "assets/strings";
import getRouting from "./routing";
import { DoctorRefinements } from "./DoctorRefinements/DoctorRefinements";
import Text from "components/text/Text";
import { checkIfParamExist, getParams } from "helpers";
import InfiniteHitsComponent from "./InfiniteHitsComponent";
import GeoInfiniteHitsComponent from "./GeoInfiniteHitsComponent";
import { VirtualRefinementList } from "../../../components/virtualRefinement/VirtualRefinmentList";
import { VirtualQuery } from "components/virtualQuery/VirtualQuery";
import { FilterButton } from "components/buttons/filterButton/FilterButton";
import Popup from "components/popup/Popup";
import { CustomSortDropdown } from "components/customSortDropdown/CustomSortDropdown";
import SvgGenerator from "components/svgGenerator/SvgGenerator";
import { objectTypeProps } from "types";
import { VirtualSortBy } from "components/virtualSortBy/virtualSortBy";

//import { HasidaLoader } from "assets/images";

const doctorsRouting = getRouting(DOCTORS, 1);
const dietRouting = getRouting(DOCTORS, 2);
const socialRouting = getRouting(DOCTORS, 3);

function NoResultsBoundary({ children, fallback }: { children: React.ReactNode; fallback: React.ReactNode }) {
  const { results } = useInstantSearch();

  const extendedResults = results as typeof results & {
    __isError?: boolean;
    __errorStatus?: number;
  };

  if (extendedResults.__isError && extendedResults.__errorStatus === 403) {
    return (
      <div className="noMessagesWrapper"
      >
        <Text text={strings.globalStrings.networkError} />
      </div>
    );
  }

  if (!results.__isArtificial && results.nbHits === 0) {
    return (
      <>
        {fallback}
        <div hidden>{children}</div>
      </>
    );
  }

  return <>{children}</>;
}

function NoResults() {
  const noResultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (noResultsRef.current) {
      noResultsRef.current.focus();
    }
  }, []);

  return (
    <div
      className="noMessagesWrapper"
      role="status"
      aria-live="assertive"
      tabIndex={-1}
      ref={noResultsRef}>
      <SvgGenerator name="Magnifier" className="magnifierIcon" objectName="doctorSearchResults" />
      <Text text={strings.globalStrings.noResultsFound} />
    </div>
  );
}



export function DoctorsSearchResults(props: objectTypeProps) {
  const { objectType } = props;
  const [isLeumit, setIsLeumit] = useState(!checkIfParamExist('typeOfDoctor'));
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 991.98);
  const routing = objectType === 1 ? doctorsRouting : (objectType === 2 ? dietRouting : socialRouting);
  const [currentSortBy, setCurrentSortBy] = useState(DOCTORS);
  const popupRef = useRef<HTMLDivElement>(null);
  const [key, setKey] = useState(0);

  const RenderResults = ({ currentSortBy }: { currentSortBy: string }) => {
    const [hitsResults, setHitsResults] = useState<{ indexName: string, total: number }[]>([
      { indexName: 'DOCTORS_INDEX', total: 0 },
      { indexName: 'exact', total: 0 },
      { indexName: 'geo', total: 0 }
    ]);
    const [doctorsHits, setDoctorsHits] = useState(0);
    const [exactHits, setExactHits] = useState(0);
    const [geoHits, setGeoHits] = useState(0);
    const { scopedResults } = useInstantSearch();

    useEffect(() => {
      function calculateExactStats() {
        let hitsIndexes = [
          { indexName: 'DOCTORS_INDEX', total: 0 },
          { indexName: 'exact', total: 0 },
          { indexName: 'geo', total: 0 }
        ];

        let doctorsIndex = scopedResults.filter(i => i.indexId === 'DOCTORS_INDEX');
        let exactindex = scopedResults.filter(i => i.indexId === 'exact');
        let geoindex = scopedResults.filter(i => i.indexId === 'geo');

        if (doctorsIndex.length) {
          hitsIndexes[0].total != doctorsIndex[0].results?.nbHits ? setDoctorsHits(doctorsIndex[0].results?.nbHits || 0) : null;
          hitsIndexes[0].total = doctorsIndex[0].results?.nbHits || 0;
        }
        if (exactindex.length) {
          hitsIndexes[1].total != exactindex[0].results?.nbHits ? setExactHits(exactindex[0].results?.nbHits || 0) : null;
          hitsIndexes[1].total = exactindex[0].results?.nbHits || 0;
        }
        if (geoindex.length) {
          hitsIndexes[2].total != geoindex[0].results?.nbHits ? setGeoHits(geoindex[0].results?.nbHits || 0) : null;
          hitsIndexes[2].total = geoindex[0].results?.nbHits || 0;
        }
        return hitsIndexes;
      }

      if (hitsResults[1].total >= 5) {
        const url = new URL(window.location.href);
        url.searchParams.delete('loc');
        url.searchParams.delete('r');
        window.history.replaceState({}, document.title, url.toString());
        setKey(prevKey => prevKey + 1); // Force re-render
        //window.location.href = url.toString();
      }

      const newHitsResults = calculateExactStats();
      setHitsResults(newHitsResults);
    }, [scopedResults, doctorsHits, exactHits, geoHits]); // Only re-run the effect if scopedResults changes

    localStorage.setItem('hitsResults', JSON.stringify(hitsResults));

    // if (status !== 'idle' && !isFinalResults) {
    //   // Render a loading spinner or placeholder while waiting for results
    //   return (
    //     <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'absolute', inset: '0', background: "rgb(255 255 255 / 76%)", paddingTop: '15rem' }} >
    //       <img src={HasidaLoader} />
    //     </div>
    //   );
    // }

    if (!checkIfParamExist('loc')) {
      return <InfiniteHitsComponent />;
    }

    else if (exactHits < 5) {
      return <>
        <Index indexName={DOCTORS} indexId="exact">
          <Configure aroundLatLng={checkIfParamExist('loc') ? getParams(['loc'])[0] : ''} aroundRadius={checkIfParamExist('r') ? Number(getParams(['r'])[0]) : 0} />
          <VirtualRefinementList attribute={algoliaAttributes.CityCode} />
          <GeoInfiniteHitsComponent />
          {geoHits > 0 && <div className="resultsAround">
            <SvgGenerator name="Exclamation" className="exclamationIcon" objectName="doctorSearchResults" />
            <Text text={strings.globalStrings.foundResultsAroundLocation} />
          </div>
          }
        </Index>
        <Index indexName={DOCTORS} indexId="geo" >
          <Configure snippetEllipsisText="…" hitsPerPage={20} aroundLatLng={checkIfParamExist('loc') ? getParams(['loc'])[0] : ''} aroundRadius={checkIfParamExist('r') ? Number(getParams(['r'])[0]) : 0} />
          <VirtualSortBy items={[{ label: "שם", value: DOCTORS }, { label: "מרחק", value: DOCTORS_SORT }]} sortBy={currentSortBy} />
          <GeoInfiniteHitsComponent />
        </Index>
      </>
    }
    else {
      return <Index indexName={DOCTORS} indexId="exact">
        <Configure snippetEllipsisText="…" hitsPerPage={20} aroundLatLng={checkIfParamExist('loc') ? getParams(['loc'])[0] : ''} aroundRadius={checkIfParamExist('r') ? Number(getParams(['r'])[0]) : 0} />
        <VirtualRefinementList attribute={algoliaAttributes.CityCode} />
        <GeoInfiniteHitsComponent />
      </Index>
    }
  }

  // const ResetOnRefresh = () => {
  //   const { setUiState } = useInstantSearch();
  //   useEffect(() => {
  //     setUiState((prevState) => ({
  //       ...prevState,
  //       [DOCTORS]: {
  //         ...prevState[DOCTORS],
  //         page: 1 // Reset only the page
  //       },
  //     }));
  //   }, []);

  //   return null;
  // };


  const handleResize = () => {
    setIsMobile(window.innerWidth < 992);
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleToggleVisibility = () => {
    if (popupRef.current) {
      popupRef.current.classList.toggle('show');
    }
  };

  return (
    <div className="row two-col-layout ais-InstantSearch" key={key}>
      <InstantSearch
        indexName={DOCTORS}
        searchClient={leumitSearchClient}
        routing={routing}
      >
        <Configure snippetEllipsisText="…" hitsPerPage={20} />
        {isMobile &&
          <Popup
            isSidePopup
            ref={popupRef}
            onClose={() => handleToggleVisibility()}
          >
            <DoctorRefinements
              isLeumit={isLeumit} objectType={objectType} />
          </Popup>

        }
        {!isMobile &&
          <DoctorRefinements
            isLeumit={isLeumit} objectType={objectType} />
        }
        <div className="col-12 col-lg-8 col-xl-9 ais-SearchResults">
          {isMobile &&
            <div className="divMobileTop">
              <FilterButton onClick={() => handleToggleVisibility()} text={strings.globalStrings.filterBy} />
              {checkIfParamExist('loc') && <CustomSortDropdown items={[
                { label: "שם", value: DOCTORS, items: [] },
                { label: "מרחק", value: DOCTORS_SORT, items: [] },
              ]}
                onSortChange={setCurrentSortBy} // Add this prop
              />
              }
            </div>
          }
          <TopRefinement isRes={JSON.parse(localStorage.getItem('hitsResults') || '[]')} objectType={objectType} setIsLeumit={setIsLeumit} setCurrentSortBy={setCurrentSortBy} />
          <VirtualRefinementList attribute={algoliaAttributes.MedicalFieldName} />
          <VirtualRefinementList attribute={algoliaAttributes.CityCode} />
          <VirtualQuery query={localAttributes.localDoctorFieldName} />


          {(objectType == 1 && !isLeumit) &&
            <div className="divExternalSortBy">
              <SvgGenerator name="Exclamation" className="icon" objectName="doctorSearchResults" />
              <Text className="text" text={strings.doctorSearchResults.exteranlSortOrder} />
            </div>}
          <NoResultsBoundary fallback={<NoResults />}>
            {/* <ResetOnRefresh /> */}
            <RenderResults currentSortBy={currentSortBy} />
          </NoResultsBoundary>
        </div>
      </InstantSearch>
    </div>
  );
}
